import {
  BIG_PARTY,
  DUNGEON,
  INCURSION,
  INCURSION_SPAWN_SPACING,
  INCURSION_TWISTS,
  MAREAS,
  NIGHTMARES,
  SAGA,
  SAGA_CHAPTERS,
  UNIQUES,
  type GearAttr,
  type NightmareType,
  type NpcId,
  type Resources,
  type SagaAct,
  type SagaBattle,
  type SagaChapter,
  type SagaChoice,
  type SagaCond,
  type SagaLine,
  type SagaScene,
  type BetId,
  type SideId,
  TOMES,
  GLYPH_FRAGS,
  FEAT_HARD_GROUP,
} from '@somnhold/content';
import { applyBet } from './bets.ts';
import { applySideBattle, sideDef } from './sides.ts';
import type { BattleParams } from '../battle/create.ts';
import { hexDist, hexRing } from '../hex.ts';
import { createDungeonMap, createIncursionMap } from '../map.ts';
import { idiv, imax, imin, mulBp } from '../math/fixed.ts';
import { bigParty, crowd } from './party.ts';
import { deriveSeed, rngChance, rngInt, rngWeighted, seedRng } from '../math/rng.ts';
import { snapshot } from '../math/hash.ts';
import type { BattleRules, BattleState, DungeonSpawn, Spawn } from '../types.ts';
import { addRes, event, findPlayer, heroName } from './core.ts';
import { giveUnique, promiseUnique } from './items.ts';
import { dragonParams, dragonRoundLost, dragonTry, isDragonAct } from './dragon.ts';
import { eraOf, groupLevel } from './eras.ts';

export { giveUnique, hasUnique } from './items.ts';
import { fightKit, grantXp, grantSagaGlyph, addGlyphFragment, addPages } from './progress.ts';
import type { CampaignState, CommandResult, Ms, PlayerState, SagaLog, SagaState } from './types.ts';
import { tallyDeeds } from './deeds.ts';
import { tallyFeats, type FeatPlace } from './feats.ts';
import { logOuting } from './outings.ts';

/**
 * The saga of the Turtle (docs/QUESTS.md): one chapter at a time for the whole group, act after act,
 * anyone may play the next one. Scenes are campaign commands; battle acts are issued like incursions,
 * re-simulated, then applied here. Provisions pay for lost battles; out of them, the chapter is lost,
 * the story takes its toll and it starts over the next day. A decision goes to the group's vote; a
 * finished chapter can be played again by anyone, told as it was, without changing what was decided.
 */

const fresh = (): SagaState => ({ chapter: 0, act: 0, provisions: SAGA.provisions, aff: {}, flags: [], played: [], tries: 0, log: { from: 0, aff: {}, flags: [], marks: [] } });

/** The saga as it stands (a fresh one if nobody opened it yet). */
export const sagaOf = (s: CampaignState): SagaState => s.saga ?? fresh();

const sagaMut = (s: CampaignState): SagaState => (s.saga ??= fresh());

/** The memory's log, started with the memory as it stands when a campaign from before it first needs it. */
const logMut = (m: SagaState): SagaLog => (m.log ??= { from: m.chapter, aff: { ...m.aff }, flags: [...m.flags], marks: [] });

export const sagaChapter = (s: CampaignState): SagaChapter | null => SAGA_CHAPTERS[sagaOf(s).chapter] ?? null;

export const sagaAct = (s: CampaignState): SagaAct | null => sagaChapter(s)?.acts[sagaOf(s).act] ?? null;

export type SagaBlock = 'over' | 'campaignLost' | 'level' | 'era' | 'resting';

/** Why the current chapter can't be played today (null: it can). After a won campaign the saga goes on. */
/**
 * A chapter's prize for a hero (docs/UNICAS.md §1): its unique piece, now or kept until the level it asks
 * (docs/PROGRESION.md), or its whole glyph, once per hero. A chapter begun before the prizes changed pays the piece it
 * paid then (`SagaState.pays`). Returns the piece when given now.
 */
const earnUnique = (s: CampaignState, p: PlayerState, ch: SagaChapter, now: Ms): ReturnType<typeof giveUnique> => {
  const idx = SAGA_CHAPTERS.indexOf(ch);
  const was = sagaOf(s).pays;
  const slot = was && was.chapter === idx ? was.unique : ch.unique;
  const level = was && was.chapter === idx ? was.level : (ch.uniqueLevel ?? 1);
  if (!slot) {
    if (ch.glyph) grantSagaGlyph(s, p.id, idx, ch.glyph, now);
    return null;
  }
  const old = idx < (s.sagaLevelsFrom ?? 0);
  if (old || p.hero.level >= level) return giveUnique(s, p, slot, now);
  promiseUnique(s, p, slot, level, now);
  return null;
};

/** Whether a finished chapter pays something to whoever plays it again and lacks it (a unique piece or a whole glyph). */
const pays = (ch: SagaChapter | undefined): boolean => !!ch?.unique || !!ch?.glyph;

export const sagaBlocked = (s: CampaignState, day: number): SagaBlock | null => {
  const ch = sagaChapter(s);
  if (!ch) return 'over';
  if (s.status === 'lost') return 'campaignLost';
  // A chapter not yet begun waits for the group's level and the bastion's era (by the campaign's own clock).
  const sg = sagaOf(s);
  const begun = sg.act > 0 || sg.played.length > 0 || !!sg.vote;
  if (!begun && ch.minGroup && groupLevel(s, s.clock) < ch.minGroup) return 'level';
  if (!begun && ch.minEra && eraOf(s) < ch.minEra) return 'era';
  const rest = sagaOf(s).restDay;
  if (rest !== undefined && day < rest) return 'resting';
  return null;
};

/** What the cast thinks and the marks the story left: the group's today, or as it was at some act. */
export interface SagaMemory {
  aff: Partial<Record<NpcId, number>>;
  flags: string[];
}

export const condMet = (sg: SagaMemory, c: SagaCond | undefined): boolean => {
  if (!c) return true;
  if (c.flag && !sg.flags.includes(c.flag)) return false;
  if (c.notFlag && sg.flags.includes(c.notFlag)) return false;
  if (c.aff && (sg.aff[c.aff[0]] ?? 0) < c.aff[1]) return false;
  if (c.affBelow && (sg.aff[c.affBelow[0]] ?? 0) >= c.affBelow[1]) return false;
  return true;
};

/** The lines of a scene this group gets to hear (some depend on what they did before); `mem` tells it as it was. */
export const sceneLines = (s: CampaignState, sc: SagaScene, mem: SagaMemory = sagaOf(s)): SagaLine[] => sc.lines.filter((l) => condMet(mem, l.if));

/** The choices open to this group in a scene. */
export const sceneChoices = (s: CampaignState, sc: SagaScene, mem: SagaMemory = sagaOf(s)): SagaChoice[] => sc.choices.filter((c) => condMet(mem, c.if));

const clampAff = (n: number): number => imax(-SAGA.affinityMax, imin(SAGA.affinityMax, n));

const canPay = (store: Resources, cost: Partial<Resources>): boolean =>
  (Object.keys(cost) as (keyof Resources)[]).every((k) => (store[k] ?? 0) >= (cost[k] ?? 0));

/** What a choice (or a lost chapter) does to the memory. */
const touch = (mem: SagaMemory, aff: Partial<Record<NpcId, number>> | undefined, set: string[] | undefined): void => {
  for (const k of Object.keys(aff ?? {}).sort() as NpcId[]) mem.aff[k] = clampAff((mem.aff[k] ?? 0) + (aff?.[k] ?? 0));
  for (const f of set ?? []) if (!mem.flags.includes(f)) mem.flags.push(f);
};

const sceneAt = (chapter: number, act: number): SagaScene | null => {
  const a = SAGA_CHAPTERS[chapter]?.acts[act];
  return a?.kind === 'scene' ? a : null;
};

/**
 * The memory as it was when the group reached an act: the log played up to it. Chapters from before
 * the log are told with today's memory.
 */
export const memoryAt = (s: CampaignState, chapter: number, act: number): SagaMemory => {
  const sg = sagaOf(s);
  const log = sg.log;
  if (!log || chapter < log.from) return { aff: { ...sg.aff }, flags: [...sg.flags] };
  const mem: SagaMemory = { aff: { ...log.aff }, flags: [...log.flags] };
  for (const mk of log.marks) {
    if (mk.chapter > chapter || (mk.chapter === chapter && mk.act >= act)) continue;
    const ch = SAGA_CHAPTERS[mk.chapter];
    if (!ch) continue;
    if (mk.lost) touch(mem, mk.kept ? undefined : ch.loss.aff, [ch.loss.set]);
    else {
      const c = sceneAt(mk.chapter, mk.act)?.choices.find((x) => x.id === mk.choice);
      if (c) touch(mem, c.aff, c.set);
    }
  }
  return mem;
};

/** A scene with more than one way out is a decision: it goes to the group's vote. */
export const isDecision = (s: CampaignState, sc: SagaScene, mem?: SagaMemory): boolean => sceneChoices(s, sc, mem).length > 1;

/**
 * What the group decided at a finished act, and who voted what. Decisions from before the log are
 * read off the marks they left (the side that leaves none, if the other did); null when it can't be told.
 */
export const sagaDecision = (s: CampaignState, chapter: number, act: number): { choice: string; votes: [string, string][] } | null => {
  const sg = sagaOf(s);
  const marks = sg.log?.marks ?? [];
  for (let i = marks.length - 1; i >= 0; i--) {
    const mk = marks[i] as (typeof marks)[number];
    if (mk.chapter === chapter && mk.act === act && mk.choice) return { choice: mk.choice, votes: mk.votes ?? [] };
  }
  if (sg.vote?.chapter === chapter && sg.vote.act === act) return null;
  const sc = sceneAt(chapter, act);
  if (!sc || (sg.log && chapter > sg.log.from)) return null;
  const marked = sc.choices.find((c) => c.set?.length && c.set.every((f) => sg.flags.includes(f)));
  if (marked) return { choice: marked.id, votes: [] };
  const plain = sc.choices.filter((c) => !c.set?.length);
  if (plain.length === 1 && plain.length < sc.choices.length) return { choice: (plain[0] as SagaChoice).id, votes: [] };
  return null;
};

/** Who gets a say in a vote: every hero still in the campaign. */
const voters = (s: CampaignState): number => s.players.filter((p) => !p.left).length;

/** What a decision does once taken: its cost, the cast, its marks, provisions (if its chapter is still the one played). */
const takeChoice = (s: CampaignState, chapter: number, act: number, c: SagaChoice, votes?: [string, string][]): void => {
  const m = sagaMut(s);
  const log = logMut(m);
  if (c.cost && canPay(s.storage, c.cost)) addRes(s.storage, c.cost, -1);
  touch(m, c.aff, c.set);
  if (c.provisions && chapter === m.chapter) m.provisions = imax(0, imin(SAGA.maxProvisions, m.provisions + c.provisions));
  log.marks.push({ chapter, act, choice: c.id, ...(votes ? { votes: votes.map(([p, x]) => [p, x] as [string, string]) } : {}) });
};

/**
 * Closes the vote if it is decided (everyone voted, or one side has more than half the group), or if
 * `force`: the most voted side wins, a tie goes to whoever opened it, a side the storage can no
 * longer pay gives way to the next one.
 */
const decide = (s: CampaignState, now: Ms, force: boolean): boolean => {
  const m = sagaMut(s);
  const v = m.vote;
  if (!v) return false;
  const sc = sceneAt(v.chapter, v.act);
  if (!sc) {
    delete m.vote;
    return true;
  }
  const count = (id: string): number => v.votes.filter((x) => x[1] === id).length;
  const n = voters(s);
  let top = 0;
  for (const c of sc.choices) top = imax(top, count(c.id));
  if (!force && v.votes.length < n && top * 2 <= n) return false;
  const opener = v.votes.find((x) => x[0] === v.by)?.[1];
  const ranked = sc.choices.filter((c) => count(c.id) > 0).sort((a, b) => count(b.id) - count(a.id) || (b.id === opener ? 1 : 0) - (a.id === opener ? 1 : 0));
  const pick = ranked.find((c) => !c.cost || canPay(s.storage, c.cost)) ?? sc.choices.find((c) => !c.cost) ?? (ranked[0] as SagaChoice);
  takeChoice(s, v.chapter, v.act, pick, v.votes);
  delete m.vote;
  event(s, now, 'saga', { pid: v.by, data: { kind: v.votes.length > 1 ? 'decided' : 'choice', chapter: v.chapter, act: v.act, choice: pick.id } });
  return true;
};

/** Closes the vote now with the votes it has (a campaign whose companions are bots: they go along). */
export const closeSagaVote = (s: CampaignState, now: Ms): boolean => decide(s, now, true);

/** A vote whose day is over closes with the votes it has. */
export const settleSagaVote = (s: CampaignState, now: Ms): boolean => {
  const v = s.saga?.vote;
  return !!v && now >= v.at + SAGA.voteMs && decide(s, now, true);
};

/**
 * A scene's choice, by whoever plays it. A decision opens the group's vote with theirs (the story goes
 * on meanwhile: battles can be played, the next scene waits for the vote); anything else is taken now.
 * A choice on the decision already up for the vote (someone opened it first) is a vote on it.
 */
export const applySagaChoice = (s: CampaignState, pid: string, act: number, choiceId: string, now: Ms, day: number, chapter?: number): CommandResult => {
  const sg = sagaOf(s);
  if (sg.vote && sg.vote.act === act && sg.vote.chapter === (chapter ?? sg.vote.chapter)) return applySagaVote(s, pid, sg.vote.chapter, act, choiceId, now);
  if (sagaBlocked(s, day)) return { ok: false, reason: 'sagaClosed' };
  if (act !== sg.act || (chapter !== undefined && chapter !== sg.chapter)) return { ok: false, reason: 'staleAct' };
  const a = sagaAct(s);
  if (!a || a.kind !== 'scene') return { ok: false, reason: 'notAScene' };
  if (sg.vote) return { ok: false, reason: 'voting' };
  const c = sceneChoices(s, a).find((x) => x.id === choiceId);
  if (!c) return { ok: false, reason: 'noChoice' };
  if (c.cost && !canPay(s.storage, c.cost)) return { ok: false, reason: 'notEnough' };
  const m = sagaMut(s);
  logMut(m);
  if (isDecision(s, a)) {
    m.vote = { chapter: m.chapter, act, by: pid, at: now, votes: [[pid, c.id]] };
    if (!decide(s, now, false)) event(s, now, 'saga', { pid, data: { kind: 'vote', chapter: m.chapter, act } });
  } else {
    takeChoice(s, m.chapter, act, c);
    event(s, now, 'saga', { pid, data: { kind: 'choice', chapter: m.chapter, act, choice: c.id } });
  }
  advance(s, [pid], now);
  return { ok: true };
};

/** A vote on the open decision (changing one's vote is fine until it closes). */
export const applySagaVote = (s: CampaignState, pid: string, chapter: number, act: number, choiceId: string, now: Ms): CommandResult => {
  const v = sagaOf(s).vote;
  if (!v || v.chapter !== chapter || v.act !== act) return { ok: false, reason: 'noVote' };
  const sc = sceneAt(chapter, act);
  const c = sc ? sceneChoices(s, sc).find((x) => x.id === choiceId) : undefined;
  if (!c) return { ok: false, reason: 'noChoice' };
  if (c.cost && !canPay(s.storage, c.cost)) return { ok: false, reason: 'notEnough' };
  const mine = v.votes.find((x) => x[0] === pid);
  if (mine) mine[1] = c.id;
  else v.votes.push([pid, c.id]);
  decide(s, now, false);
  return { ok: true };
};

/** The act is done: on to the next, or the chapter is. */
const advance = (s: CampaignState, pids: string[], now: Ms): void => {
  const m = sagaMut(s);
  for (const pid of pids) if (!m.played.includes(pid)) m.played.push(pid);
  m.act++;
  const ch = SAGA_CHAPTERS[m.chapter] as SagaChapter;
  if (m.act < ch.acts.length) return;
  // The chapter is done: the storage fills, everyone who played an act gets XP and the unique piece.
  addRes(s.storage, ch.final ? SAGA.finalReward : SAGA.reward);
  const done = (m.done ??= []);
  while (done.length <= m.chapter) done.push([]);
  done[m.chapter] = [...m.played].sort();
  for (const pid of [...m.played].sort()) {
    grantXp(s, pid, ch.final ? SAGA.finalXp : SAGA.xp, now);
    addPages(s, pid, 2, TOMES.saga, now);
    addGlyphFragment(s, pid, 'boss', GLYPH_FRAGS.saga, now);
    const p = findPlayer(s, pid);
    // The chapter's unique piece, kept until the hero reaches the level it asks.
    if (p) earnUnique(s, p, ch, now);
  }
  event(s, now, 'saga', { data: { kind: 'chapter', chapter: m.chapter, played: [...m.played].sort().join(',') } });
  if (m.pays?.chapter === m.chapter) delete m.pays;
  m.chapter++;
  m.act = 0;
  m.provisions = SAGA.provisions;
  m.played = [];
  delete m.restDay;
};

/**
 * Campaigns from before the saga walked with the heroes (2026-10-28): the chapter they were playing, if begun,
 * pays as before; the level rules start with the next one. Idempotent; runs when a campaign loads.
 */
export const ensureSagaLevels = (s: CampaignState): boolean => {
  if (s.sagaLevelsFrom !== undefined) return false;
  const sg = sagaOf(s);
  const begun = sg.act > 0 || sg.played.length > 0 || !!sg.vote;
  s.sagaLevelsFrom = begun ? sg.chapter + 1 : sg.chapter;
  return true;
};

/**
 * Unique pieces given before 2026-10-26 had two attributes and no effect: they get their third (the
 * first of the slot's they lack, at its value) and their own effect. Idempotent; runs when a campaign loads.
 */
export const ensureUniques = (s: CampaignState): boolean => {
  let changed = false;
  for (const p of s.players) {
    for (const item of [p.hero.gear.weapon, p.hero.gear.armor, p.hero.gear.amulet, ...p.hero.bag]) {
      if (!item?.unique) continue;
      const def = UNIQUES[item.unique];
      if (item.effect !== def.effect) {
        item.effect = def.effect;
        changed = true;
      }
      const want = Object.keys(def.attrs).length;
      // Newest first: the slot's third attribute, unless a reforge already brought it.
      for (const k of (Object.keys(def.attrs) as GearAttr[]).reverse()) {
        if (Object.keys(item.attrs).length >= want) break;
        if (item.attrs[k] !== undefined) continue;
        item.attrs[k] = def.attrs[k] as number;
        changed = true;
      }
    }
  }
  return changed;
};

/** The unique pieces a hero wears (their effects ride into battle). */
export const uniquesWorn = (p: PlayerState): string[] =>
  [p.hero.gear.weapon, p.hero.gear.armor, p.hero.gear.amulet].flatMap((x) => (x?.unique && UNIQUES[x.unique].cls === p.hero.heroClass ? [x.unique] : []));

/* ---------- battle acts ---------- */

/** What a battle act was issued for: applying it checks the saga is still there. */
export interface SagaTicket {
  chapter: number;
  act: number;
  /** A finished chapter replayed for a missing unique piece (older clients: only its last battle). */
  replay?: boolean;
  /** A finished chapter played again from the start, by this hero. */
  rerun?: string;
  /** One of Brann and Hilde's bets (campaign/bets.ts): chapter and act are 0. */
  bet?: BetId;
  /** A side saga's act (campaign/sides.ts): chapter and act are that saga's. */
  side?: SideId;
}

export const party = (s: CampaignState, pids: string[]): string[] => {
  const out: string[] = [];
  for (const pid of pids) if (!out.includes(pid) && findPlayer(s, pid)) out.push(pid);
  return out.slice(0, DUNGEON.maxHeroes);
};

/** Party scaling: by heroes, and by their average level; past four heroes part of it as tougher nightmares (`hpBp`, BIG_PARTY). */
const scaleOf = (s: CampaignState, pids: string[]): { all: number; level: number; avg: number; hpBp: number } => {
  const n = pids.length || 1;
  let lv = 0;
  for (const pid of pids) lv += findPlayer(s, pid)?.hero.level ?? 1;
  const avg100 = idiv(lv * 100, n);
  const level = 10000 + idiv((avg100 - 100) * SAGA.perLevelBp, 100);
  const big = crowd(SAGA.partyBp, n, SAGA.big);
  return { all: mulBp(big.count, level), level, avg: idiv(avg100, 100), hpBp: big.hpBp };
};

const heroesOf = (s: CampaignState, pids: string[]): BattleParams['heroes'] =>
  pids.map((pid, i) => {
    const p = findPlayer(s, pid) as PlayerState;
    return { pid, name: heroName(p), cls: p.hero.heroClass, level: p.hero.level, control: 'live' as const, powerBp: 10000, post: i % 3, relics: p.hero.relics, awakened: p.hero.awakened ?? [], ...fightKit(p.hero, 'saga', s) };
  });

const bossHp = (b: NonNullable<SagaBattle['boss']>, n: number, level: number, four = 10000): number => mulBp(mulBp(bigParty(b.hp, imax(1, n), SAGA.bigBoss), level), n >= 4 ? four : 10000);

/** Some acts are easier for a party of four: they bring this much more. */
const fourOf = (act: SagaBattle, n: number): number => (n >= 4 ? (act.fourBp ?? 10000) : 10000);

/**
 * Battle for the act being played (or a finished chapter's last battle, replayed for a unique piece). The dragon's
 * fight brings the absent friends picked (`ghosts`) as spectres (docs/UNICAS.md §4.11).
 */
export const sagaBattleParams = (s: CampaignState, pids: string[], day: number, replay?: number, ghosts: string[] = []): { params: BattleParams; ticket: SagaTicket } | null => {
  const sg = sagaOf(s);
  let chapter = sg.chapter;
  let actIdx = sg.act;
  if (replay !== undefined) {
    if (replay >= sg.chapter || !pays(SAGA_CHAPTERS[replay])) return null;
    const acts = (SAGA_CHAPTERS[replay] as SagaChapter).acts;
    chapter = replay;
    actIdx = acts.map((a) => a.kind).lastIndexOf('battle');
  } else if (sagaBlocked(s, day)) return null;
  const who = party(s, pids);
  const params = battleFor(s, chapter, actIdx, who, deriveSeed(s.seed, 20000 + chapter * 4096 + actIdx * 512 + (sg.tries % 512)), sg, ghosts);
  return params ? { params, ticket: { chapter, act: actIdx, ...(replay !== undefined ? { replay: true } : {}) } } : null;
};

/**
 * Battle for the act a hero is at in a finished chapter they play again (told with the memory of then); friends
 * who come along fight beside them (`helpers`): it is played again because someone missed it.
 */
export const rerunBattleParams = (s: CampaignState, pid: string, helpers: string[] = [], ghosts: string[] = []): { params: BattleParams; ticket: SagaTicket } | null => {
  const r = sagaRerun(s, pid);
  if (!r) return null;
  const params = battleFor(s, r.chapter, r.act, party(s, [pid, ...helpers]), deriveSeed(s.seed, 90000 + r.chapter * 4096 + r.act * 512 + (r.tries % 512)), memoryAt(s, r.chapter, r.act), ghosts);
  return params ? { params, ticket: { chapter: r.chapter, act: r.act, rerun: pid } } : null;
};

const battleFor = (s: CampaignState, chapter: number, actIdx: number, who: string[], seed: number, mem: SagaMemory, ghosts: string[] = []): BattleParams | null => {
  const act = SAGA_CHAPTERS[chapter]?.acts[actIdx];
  if (!act || act.kind !== 'battle' || !who.length) return null;
  const tough = SAGA_CHAPTERS[chapter]?.toughBp ?? 10000;
  // The dragon of the Burn: its own clearing (campaign/dragon.ts).
  if (isDragonAct(act)) return snapshot(dragonParams(s, act, who, ghosts, seed, tough, mem));
  return snapshot(act.mode === 'descent' ? descent(s, act, who, seed, tough) : field(s, act, who, seed, tough));
};

/** Hold, escort and boss acts: an incursion clearing with three waves. */
export const field = (s: CampaignState, act: SagaBattle, who: string[], seed: number, tough: number): BattleParams => {
  const map = createIncursionMap(seed);
  const rng = seedRng(seed ^ 0x5a6a);
  const k = scaleOf(s, who);
  const def = MAREAS[imin(s.mareaIndex, MAREAS.length - 1)] as (typeof MAREAS)[number];
  // An escort is a running fight: no siege engines or tower-shooting archers on the road.
  const entries = (Object.keys(def.weights) as NightmareType[])
    .sort()
    .filter((t) => act.mode !== 'escort' || (!NIGHTMARES[t].siege && !NIGHTMARES[t].shootsTowers))
    .map((t) => [t, def.weights[t] as number] as const);
  const eliteKind = entries.reduce((a, b) => (NIGHTMARES[b[0]].points > NIGHTMARES[a[0]].points ? b : a))[0];
  const big = crowd(act.mode === 'hold' ? SAGA.holdPartyBp : SAGA.partyBp, who.length, SAGA.big);
  const budget = mulBp(mulBp(mulBp(INCURSION.budgetBase, 10000 + k.avg * INCURSION.budgetLevelBp), act.budgetBp), big.count);
  const onPath = new Set(map.sectors.flatMap((sec) => sec.path.map((h) => `${h.q},${h.r}`)));
  const woods = act.land === 'forest' ? hexRing(map.bastion).filter((h) => !onPath.has(`${h.q},${h.r}`)) : [];
  const spawns: Spawn[] = [];
  const third = idiv(INCURSION.extractTicks, SAGA.waves);
  // An escort's caravan starts at the far end of the first road: nothing comes out of that one.
  const escort = act.mode === 'escort';
  const sectorOf = (): number => (escort ? 1 + rngInt(rng, map.sectors.length - 1) : rngInt(rng, map.sectors.length));
  for (let w = 0; w < SAGA.waves; w++) {
    const start = spawns.length;
    let left = budget;
    let i = 0;
    while (left > 0 && i < 120) {
      const fit = entries.filter(([t]) => NIGHTMARES[t].points <= left);
      if (!fit.length) break;
      const kind = rngWeighted(rng, fit);
      left -= NIGHTMARES[kind].points;
      const sp: Spawn = { tick: i, sector: sectorOf(), kind, tier: rngChance(rng, act.inkBp ?? 0) ? 2 : 1, elite: false };
      if (woods.length && rngChance(rng, INCURSION_TWISTS.forest.ambushBp)) sp.at = woods[rngInt(rng, woods.length)] as { q: number; r: number };
      spawns.push(sp);
      i++;
    }
    // The last wave is closed by elites, more for a bigger party.
    const elites = w === SAGA.waves - 1 ? 1 + idiv(who.length, 2) : 0;
    for (let e = 0; e < elites; e++) spawns.push({ tick: i + 2 + e * 4, sector: sectorOf(), kind: eliteKind, tier: 2, elite: true });
    const slots = elites ? i + 3 + (elites - 1) * 4 : i;
    const step = imin(INCURSION_SPAWN_SPACING, idiv(third - 100, imax(1, slots)));
    for (let j = start; j < spawns.length; j++) (spawns[j] as Spawn).tick = 60 + w * third + (spawns[j] as Spawn).tick * step;
  }
  spawns.sort((a, b) => a.tick - b.tick || a.sector - b.sector);
  const rules: BattleRules = { twist: act.land ?? 'quarry', turrets: { ...INCURSION.turrets }, clearToFinish: true, eliteHpBp: INCURSION.eliteHpBp, enemyHpBp: mulBp(mulBp(mulBp(mulBp(SAGA.enemyHpBp, k.level), fourOf(act, who.length)), tough), big.hpBp) };
  if (act.land === 'crystal') rules.rush = true;
  let objectiveTicks = INCURSION.extractTicks;
  if (escort) {
    const e = SAGA.escort;
    rules.escort = { hp: mulBp(mulBp(mulBp(e.cartHp, k.level), 10000 + (imin(who.length, BIG_PARTY.measured) - 1) * e.cartPartyBp), act.cartHpBp ?? 10000), draw: e.draw, stopRange: e.stopRange, speed: e.speed };
    objectiveTicks = 0;
  }
  if (act.mode === 'boss' && act.boss) {
    // The boss comes out with the first wave and the act lasts until it falls (or the node does).
    spawns.unshift({ tick: 90, sector: rngInt(rng, map.sectors.length), kind: act.boss.kind, tier: act.boss.tier, elite: act.boss.kind in NIGHTMARES && NIGHTMARES[act.boss.kind].points > 0, hp: mulBp(bossHp(act.boss, who.length, k.level, act.fourBp), tough), boss: true });
    rules.bossWins = true;
    objectiveTicks = 12 * 60 * 30;
  }
  return {
    mode: 'incursion',
    seed,
    map,
    structures: [],
    heroes: heroesOf(s, who),
    // Past four heroes the nightmares are tougher (BIG_PARTY) and stand at the node longer: it holds half as much more.
    keepHp: mulBp(act.mode === 'boss' ? SAGA.bossNodeHp : 1500, 10000 + idiv(big.hpBp - 10000, 2)),
    keepMaxHp: mulBp(act.mode === 'boss' ? SAGA.bossNodeHp : 1500, 10000 + idiv(big.hpBp - 10000, 2)),
    marea: s.mareaIndex,
    wave: 0,
    spawns,
    ...(objectiveTicks ? { objectiveTicks } : {}),
    ...(act.mode === 'hold' ? { objectiveQuietRate: INCURSION.quietRate } : {}),
    rules,
  };
};

/** A descent: crypt rooms with the act's own bestiary, the Warden at the end only if the act says so. */
export const descent = (s: CampaignState, act: SagaBattle, who: string[], seed: number, tough: number): BattleParams => {
  const rooms = act.rooms ?? DUNGEON.rooms;
  const map = createDungeonMap(seed, rooms);
  const L = map.dungeon;
  const rng = seedRng(seed ^ 0xd35c);
  const k = scaleOf(s, who);
  const beasts = act.bestiary ?? (DUNGEON.bestiary[0] as Partial<Record<NightmareType, number>>);
  const entries = (Object.keys(beasts) as NightmareType[]).sort().map((t) => [t, beasts[t] as number] as const);
  const spawns: DungeonSpawn[] = [];
  for (let i = 1; L && i <= rooms; i++) {
    const room = L.rooms[i];
    const door = L.doors[i - 1];
    if (!room || !door) continue;
    const spots: { q: number; r: number }[] = [];
    for (let rr = 1; rr < room.radius; rr++) for (const h of hexRing(rr)) spots.push({ q: room.q + h.q, r: room.r + h.r });
    const far = spots.filter((h) => hexDist(h, door) >= 3);
    // Every room a little worse than the one before.
    const points = mulBp(mulBp(mulBp(16 + i * 5, act.budgetBp), k.all), act.boss ? 10000 : fourOf(act, who.length));
    for (let w = 0; w < DUNGEON.wavesPerRoom; w++) {
      let left = points;
      let guard = 0;
      while (left > 0 && guard++ < 60) {
        const fit = entries.filter(([t]) => NIGHTMARES[t].points <= left);
        if (!fit.length) break;
        const kind = rngWeighted(rng, fit);
        left -= NIGHTMARES[kind].points;
        const at = far[rngInt(rng, far.length)] as { q: number; r: number };
        spawns.push({ room: i, wave: w, kind, tier: rngChance(rng, act.inkBp ?? 0) ? 2 : 1, elite: false, q: at.q, r: at.r });
      }
    }
    if (i === rooms) {
      for (let e = 0; e < 1 + idiv(who.length, 2); e++) {
        const at = far[rngInt(rng, far.length)] as { q: number; r: number };
        spawns.push({ room: i, wave: DUNGEON.wavesPerRoom - 1, kind: 'skeleton', tier: 2, elite: true, q: at.q, r: at.r });
      }
    }
  }
  return {
    mode: 'dungeon',
    seed,
    map,
    structures: [],
    heroes: heroesOf(s, who),
    keepHp: 1,
    keepMaxHp: 1,
    marea: s.mareaIndex,
    wave: 0,
    spawns: [],
    dungeon: { spawns, bossHp: act.boss ? mulBp(bossHp(act.boss, who.length, k.level, act.fourBp), tough) : 0, dmgBp: mulBp(k.level, SAGA.dmgBp), ...(act.boss ? {} : { hallWins: true }) },
    ...(mulBp(tough, k.hpBp) !== 10000 ? { rules: { enemyHpBp: mulBp(tough, k.hpBp) } } : {}),
  };
};

/**
 * Where a saga fight was, for the unique pieces' feats (docs/UNICAS.md §2.2, §2.3): a bet counts for nothing, a finished
 * chapter played again only for the Legend, and a hard or heroic side chapter from group 21 is a hard place.
 */
const featPlace = (t: SagaTicket): FeatPlace => {
  if (t.bet) return { skip: true };
  if (t.replay || t.rerun !== undefined) return { legend: true };
  if (t.side) {
    const def = sideDef(t.side);
    const ch = def?.chapters[t.chapter];
    return { hard: !!def && (def.difficulty === 'hard' || def.difficulty === 'heroic') && (ch?.minGroup ?? 0) >= FEAT_HARD_GROUP };
  }
  return {};
};

export interface SagaResult {
  /** The act counted (it moved the saga on, or cost a provision). */
  counted: boolean;
  won: boolean;
  /** The chapter was lost (out of provisions). */
  lost?: boolean;
  unique?: number;
}

/**
 * Applies a re-simulated battle act. A win moves the saga on; a loss costs a provision, and the last
 * one loses the chapter. A ticket the saga already moved past (someone else played it first) only
 * pays the fighters' XP.
 */
export const applySagaBattle = (s: CampaignState, pids: string[], ticket: SagaTicket, battle: BattleState, now: Ms, day: number): SagaResult => {
  tallyDeeds(s, 'saga', battle, now);
  tallyFeats(s, battle, featPlace(ticket), now);
  const won = battle.result === 'won';
  const who = party(s, pids);
  logOuting(s, now, ticket.bet ? `bet:${ticket.bet}` : ticket.side ? `side:${ticket.side}` : 'saga', who, won, ticket.chapter + 1);
  if (ticket.bet) return applyBet(s, who, ticket.bet, won, now);
  if (ticket.side) return applySideBattle(s, who, ticket, battle, now);
  if (ticket.rerun !== undefined) {
    const pid = ticket.rerun;
    const r = sagaRerun(s, pid);
    if (!r || r.chapter !== ticket.chapter || r.act !== ticket.act) return { counted: false, won };
    r.tries++;
    if (!won) return { counted: true, won };
    if (rerunPays(s, pid, r.chapter)) grantXp(s, pid, SAGA.actXp, now);
    // Friends who win the chapter's last battle beside its owner get the unique piece of their class they lack.
    const ch = SAGA_CHAPTERS[r.chapter] as SagaChapter;
    if (pays(ch) && r.act === ch.acts.map((a) => a.kind).lastIndexOf('battle'))
      for (const f of who) {
        const p = f !== pid ? findPlayer(s, f) : undefined;
        if (p) earnUnique(s, p, ch, now);
      }
    const unique = rerunAdvance(s, pid, now);
    return { counted: true, won, ...(unique !== null ? { unique } : {}) };
  }
  if (ticket.replay) {
    let unique: number | undefined;
    if (won && pays(SAGA_CHAPTERS[ticket.chapter]))
      for (const pid of who) {
        const p = findPlayer(s, pid);
        const item = p ? earnUnique(s, p, SAGA_CHAPTERS[ticket.chapter] as SagaChapter, now) : null;
        if (item && unique === undefined) unique = item.id;
      }
    event(s, now, 'saga', { pid: who[0] ?? '', data: { kind: 'replay', chapter: ticket.chapter, won } });
    return { counted: false, won, ...(unique !== undefined ? { unique } : {}) };
  }
  const sg = sagaOf(s);
  const current = sg.chapter === ticket.chapter && sg.act === ticket.act && !sagaBlocked(s, day);
  if (won) for (const pid of who) grantXp(s, pid, SAGA.actXp, now);
  if (!current) return { counted: false, won };
  const m = sagaMut(s);
  m.tries++;
  const atDragon = isDragonAct(SAGA_CHAPTERS[m.chapter]?.acts[m.act]);
  // The dragon: its scars, the hint for the next try, the spectres' share (campaign/dragon.ts).
  if (atDragon) dragonTry(s, m, SAGA_CHAPTERS[m.chapter] as SagaChapter, who, battle, now);
  event(s, now, 'saga', { pid: who[0] ?? '', data: { kind: 'act', chapter: ticket.chapter, act: ticket.act, won, party: who.join(',') } });
  if (won) {
    advance(s, who, now);
    return { counted: true, won };
  }
  for (const pid of who) if (!m.played.includes(pid)) m.played.push(pid);
  m.provisions--;
  if (m.provisions > 0) return { counted: true, won };
  // Out of provisions: the story takes its toll and the chapter starts over tomorrow. Lost at the dragon, it starts over
  // from the dragon (the flight won stays won), with the gate's provision again and without Odna's mind changing
  // (docs/UNICAS.md §4.8): the scene that made up for it isn't played again.
  const ch = SAGA_CHAPTERS[m.chapter] as SagaChapter;
  if (ch.loss.storage) {
    const take: Partial<Resources> = {};
    for (const k of Object.keys(ch.loss.storage).sort() as (keyof Resources)[]) take[k] = imin(s.storage[k] ?? 0, ch.loss.storage[k] ?? 0);
    addRes(s.storage, take, -1);
  }
  logMut(m).marks.push({ chapter: m.chapter, act: -1, lost: true, ...(atDragon ? { kept: true as const } : {}) });
  touch(m, atDragon ? undefined : ch.loss.aff, [ch.loss.set]);
  if (atDragon) dragonRoundLost(s, m, ch, now);
  m.act = atDragon ? m.act : 0;
  m.provisions = SAGA.provisions + (atDragon && m.flags.includes('gate.open') ? 1 : 0);
  m.restDay = day + 1;
  event(s, now, 'saga', { data: { kind: 'lost', chapter: m.chapter } });
  return { counted: true, won, lost: true };
};

/* ---------- finished chapters, played again ---------- */

/** The finished chapter a hero is playing again, and the act they are at (null: none). */
export const sagaRerun = (s: CampaignState, pid: string): { chapter: number; act: number; tries: number } | null => sagaOf(s).reruns?.[pid] ?? null;

/** Whether playing a finished chapter again still pays this hero (once: whoever played an act of it got it then). */
export const rerunPays = (s: CampaignState, pid: string, chapter: number): boolean => !(sagaOf(s).done?.[chapter] ?? []).includes(pid);

/** Starts playing a finished chapter again, from its first act (any time, as often as wanted). */
export const applySagaRerun = (s: CampaignState, pid: string, chapter: number): CommandResult => {
  if (chapter < 0 || chapter >= sagaOf(s).chapter || !SAGA_CHAPTERS[chapter]) return { ok: false, reason: 'notDone' };
  (sagaMut(s).reruns ??= {})[pid] = { chapter, act: 0, tries: 0 };
  return { ok: true };
};

/** A scene of a chapter played again: nothing to choose (the group did), on to the next act. */
export const applySagaRerunNext = (s: CampaignState, pid: string, chapter: number, act: number, now: Ms): CommandResult => {
  const r = sagaRerun(s, pid);
  if (!r || r.chapter !== chapter || r.act !== act) return { ok: false, reason: 'staleAct' };
  if (!sceneAt(chapter, act)) return { ok: false, reason: 'notAScene' };
  rerunAdvance(s, pid, now);
  return { ok: true };
};

/**
 * On to the next act of a chapter played again; at its end, the chapter's XP (once) and the unique
 * piece of the hero's class if they lack it. No materials: the storage got them the first time.
 */
const rerunAdvance = (s: CampaignState, pid: string, now: Ms): number | null => {
  const m = sagaMut(s);
  const r = m.reruns?.[pid];
  if (!r) return null;
  r.act++;
  const ch = SAGA_CHAPTERS[r.chapter] as SagaChapter;
  if (r.act < ch.acts.length) return null;
  delete m.reruns?.[pid];
  if (rerunPays(s, pid, r.chapter)) {
    grantXp(s, pid, ch.final ? SAGA.finalXp : SAGA.xp, now);
    const done = (m.done ??= []);
    while (done.length <= r.chapter) done.push([]);
    done[r.chapter] = [...(done[r.chapter] ?? []), pid].sort();
  }
  const p = findPlayer(s, pid);
  const item = p && pays(ch) ? earnUnique(s, p, ch, now) : null;
  event(s, now, 'saga', { pid, data: { kind: 'rerun', chapter: r.chapter } });
  return item ? item.id : null;
};
